import type { ButtonHTMLAttributes, InputHTMLAttributes, ReactNode } from 'react';

export function Logo({ size = 'md' }: { size?: 'sm' | 'md' | 'lg' }) {
  const heights = { sm: 'h-5', md: 'h-7', lg: 'h-10' } as const;
  return (
    <img
      src="/brand/logo.png"
      alt="Auditoria Detailing"
      className={`${heights[size]} w-auto select-none`}
      draggable={false}
    />
  );
}

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'ghost' | 'quiet';
  loading?: boolean;
}

export function Button({ variant = 'primary', loading, children, className = '', ...rest }: ButtonProps) {
  const styles = {
    primary: 'bg-brand text-black hover:bg-brand-soft active:translate-y-px',
    ghost: 'border border-line bg-raised text-ink hover:border-ink-faint active:translate-y-px',
    quiet: 'text-ink-dim hover:text-ink',
  } as const;
  return (
    <button
      className={`inline-flex min-h-12 items-center justify-center gap-2 rounded-2xl px-5 text-[0.95rem] font-semibold transition-all duration-150 disabled:pointer-events-none disabled:opacity-40 ${styles[variant]} ${className}`}
      disabled={loading || rest.disabled}
      {...rest}
    >
      {loading && <span className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />}
      {children}
    </button>
  );
}

interface FieldProps extends InputHTMLAttributes<HTMLInputElement> {
  label: string;
  hint?: string | undefined;
}

export function Field({ label, hint, id, className = '', ...rest }: FieldProps) {
  const fieldId = id ?? rest.name ?? label.toLowerCase().replace(/\s+/g, '-');
  return (
    <div className="flex flex-col gap-2">
      <label htmlFor={fieldId} className="text-[0.7rem] font-semibold uppercase tracking-[0.14em] text-ink-faint">
        {label}
      </label>
      <input
        id={fieldId}
        className={`min-h-12 w-full rounded-2xl border border-line bg-raised px-4 text-[1.05rem] text-ink transition-colors placeholder:text-ink-faint focus:border-brand focus:bg-muted focus:outline-none ${className}`}
        {...rest}
      />
      {hint && <p className="text-xs leading-relaxed text-ink-faint">{hint}</p>}
    </div>
  );
}

export function Card({ children, className = '' }: { children: ReactNode; className?: string }) {
  return <div className={`rounded-2xl border border-line bg-surface p-4 ${className}`}>{children}</div>;
}

export function Amount({
  value, className = '', symbolClassName = '',
}: { value: string; className?: string; symbolClassName?: string }) {
  const negative = value.startsWith('−');
  const digits = value.replace(/^−?₾/, '');
  return (
    <span className={`tabular tracking-tight ${className}`}>
      {negative && '−'}
      <span className={`mr-[0.08em] align-[0.08em] text-[0.58em] font-semibold opacity-60 ${symbolClassName}`}>
        ₾
      </span>
      {digits}
    </span>
  );
}

export function Skeleton({ className = '' }: { className?: string }) {
  return <div className={`animate-pulse rounded-2xl border border-line bg-surface ${className}`} />;
}

export function Spinner() {
  return (
    <span className="h-5 w-5 animate-spin rounded-full border-2 border-brand border-t-transparent" />
  );
}
